import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import MarketplaceSearchRequestSchema from "marketplace/_schema/marketplace-search-request-schema";
import { z } from "zod";

/**
 * The API writes every field of a saved search's criteria, unset ones as null. The request schema
 * reads an unset text field as absent, so the nulls are dropped before it reads them.
 */
function withoutNulls(value: unknown): unknown {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return value;
  return Object.fromEntries(Object.entries(value).filter(([, field]) => field !== null));
}

export type SavedSearchJSON = z.infer<typeof SavedSearchSchema.SavedSearchJSONZ>;

/** The body of `POST marketplace/saved-searches`. */
export type SaveSearchRequestJSON = {
  name: string;
  alertsEnabled: boolean;
  criteria: z.input<typeof MarketplaceSearchRequestSchema.MarketplaceSearchRequestZ>;
};

/** The body of `PUT marketplace/saved-searches/{id}`: the whole of what may change. */
export type UpdateSavedSearchRequestJSON = {
  name: string;
  alertsEnabled: boolean;
};

export default class SavedSearchSchema {
  public static readonly MaximumNameLength = 100;

  /** How many saved searches one user may keep. */
  public static readonly MaximumPerUser = 25;

  public static readonly SavedSearchJSONZ = z.object({
    id: z.uuid(),
    name: z.string().min(1),
    alertsEnabled: z.boolean(),
    createdDate: z.string(),
    criteria: z.preprocess(withoutNulls, MarketplaceSearchRequestSchema.MarketplaceSearchRequestZ),
  });

  public static validateJSON(json: unknown): SavedSearchJSON {
    return parseOrThrow(SavedSearchSchema.SavedSearchJSONZ, json, {
      logError: true,
      schemaName: "SavedSearchSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is SavedSearchJSON {
    return SavedSearchSchema.SavedSearchJSONZ.safeParse(json).success;
  }
}
